import { useState } from 'react'
import {
  Wallet, RefreshCw, Download, CheckCircle2, Clock, ArrowDownUp, RotateCcw,
} from 'lucide-react'
import {
  getFinanceJournal, exportFinanceJournal, getAdminPlans, resetFinanceStats,
} from '../../services/api'
import {
  C, Card, Row, Btn, Chips, Loading, ErrorLine, Empty, selectStyle, labelStyle,
  useAsync, fmtDateTime, fmtMoney, fmtInt, userLabel, Pill, NETWORK_LABELS,
  confirmAsync,
} from './adminUI'
import { StarsBalanceCard, StarsPricingCard } from './StarsFinance'

// Раздел «Финансы»: журнал оплат для админа. В отличие от «Оплат» (там заявки
// подтверждают/отклоняют) здесь — транзакционный реестр: фильтры по периоду/
// сети/тарифу/статусу, сводные карточки, разбивки и экспорт CSV. Данные — те же
// payment_requests, что и в выручке «Статистики», но в журнале видна каждая
// транзакция, а не только итоги.
//
// С 2026-09-17 (task.txt п.8) сюда же перенесены карточки Telegram Stars из
// раздела «Система»: «Баланс Stars бота» и «Цена Telegram Stars». Цена Stars
// правится через cfg.starsPricing и сохраняется кнопкой внизу раздела (как в
// «Системе»), поэтому раздел принимает те же props, что и SystemSection.

const PERIODS = [
  { key: 'all',   label: 'Всё время' },
  { key: 'today', label: 'Сегодня' },
  { key: '7d',    label: '7 дней' },
  { key: '30d',   label: '30 дней' },
  { key: 'month', label: 'Этот месяц' },
]

const STATUS = {
  pending:   { label: 'Ожидает',   color: C.warn },
  paid:      { label: 'Оплачено',  color: C.accent },
  expired:   { label: 'Истекла',   color: C.dim },
  failed:    { label: 'Отклонена', color: C.red },
  cancelled: { label: 'Отменена',  color: C.dim },
}

export default function FinanceSection({ cfg, setGroup }) {
  const [period, setPeriod] = useState('all')
  const [status, setStatus] = useState('')
  const [network, setNetwork] = useState('')
  const [plan, setPlan] = useState('')
  const [exporting, setExporting] = useState(false)
  const [openId, setOpenId] = useState(null)
  // Сброс статистики (см. resetFinanceStats на бэкенде): includeHistory —
  // чекбокс «показать записи до сброса», resetting — блокировка кнопок на
  // время запроса, чтобы сброс не улетел дважды.
  const [includeHistory, setIncludeHistory] = useState(false)
  const [resetting, setResetting] = useState(false)

  const { data, loading, error, reload } = useAsync(
    () => getFinanceJournal({
      period, status, network, plan, limit: 200,
      // '1' включает снятие метки сброса; '' — параметр не уходит вовсе (qs
      // пропускает пустые значения), поэтому по умолчанию фильтр активен.
      includeHistory: includeHistory ? '1' : '',
    }),
    [period, status, network, plan, includeHistory],
  )
  const plans = useAsync(() => getAdminPlans(), [])

  const t = data?.totals || {}
  const networks = data?.byNetwork || []

  const networksChips = [
    { key: '', label: 'Все сети' },
    ...networks.map(n => ({
      key: n.network,
      label: NETWORK_LABELS[n.network] || n.network,
      count: n.c,
    })),
  ]
  const statusChips = [
    { key: '',    label: 'Все статусы' },
    { key: 'paid',    label: 'Оплачено' },
    { key: 'pending', label: 'Ожидают' },
    { key: 'failed',  label: 'Отклонены' },
    { key: 'expired', label: 'Истекли' },
  ]
  const plansOptions = ((plans.data || {}).plans || []).map(p => p.code)

  const doExport = async () => {
    if (exporting) return
    setExporting(true)
    try {
      const csv = await exportFinanceJournal({ period, status, network, plan })
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `finance_journal_${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      console.error('[Finance] Export failed:', e)
      alert(`Не удалось выгрузить CSV: ${e?.message || e}`)
    } finally {
      setExporting(false)
    }
  }

  // Сброс статистики журнала. Неразрушающий: записи оплат не удаляются —
  // на бэкенде в app_config ставится метка времени, и от неё считаются сводки
  // и разбивки (а также выручка в «Статистике»). Отменяется кнопкой «вернуть».
  const doReset = async () => {
    if (resetting) return
    const ok = await confirmAsync(
      'Сбросить статистику раздела «Финансы»?\n\n' +
      'Обнулятся сводки, разбивки по сетям, тарифам и дням, а также выручка ' +
      'в разделе «Статистика». Цифры начнут считаться заново с текущего момента.\n\n' +
      'Записи об оплатах НЕ удаляются: реестр транзакций, экспорт CSV и история ' +
      'оплат у юзеров сохранятся. Сброс можно отменить — «Вернуть как было».',
    )
    if (!ok) return
    setResetting(true)
    try {
      await resetFinanceStats(true)
      setIncludeHistory(false)
      reload()
    } catch (e) {
      console.error('[Finance] Reset failed:', e)
      alert(`Не удалось сбросить статистику: ${e?.message || e}`)
    } finally {
      setResetting(false)
    }
  }

  const undoReset = async () => {
    if (resetting) return
    const ok = await confirmAsync(
      'Отменить сброс статистики?\n\nЦифры снова будут считаться за всё время.',
    )
    if (!ok) return
    setResetting(true)
    try {
      await resetFinanceStats(false)
      setIncludeHistory(false)
      reload()
    } catch (e) {
      console.error('[Finance] Reset undo failed:', e)
      alert(`Не удалось отменить сброс: ${e?.message || e}`)
    } finally {
      setResetting(false)
    }
  }

  const rows = data?.rows || []

  return (
    <div>
      {/* Telegram Stars (task.txt п.8): карточки перенесены из «Системы».
          Цена Stars правится через cfg.starsPricing и сохраняется кнопкой
          «Сохранить настройки» внизу раздела. */}
      <StarsBalanceCard />
      <StarsPricingCard cfg={cfg} setGroup={setGroup} plans={plans} />

      <Chips options={PERIODS} value={period} onChange={setPeriod} />

      {/* Фильтры по статусу и сети */}
      <Chips options={statusChips} value={status} onChange={setStatus} />
      <Chips options={networksChips} value={network} onChange={setNetwork} />

      {/* Фильтр по тарифу */}
      <div style={{ marginBottom: 12 }}>
        <div style={labelStyle}>Тариф</div>
        <select
          value={plan}
          onChange={e => setPlan(e.target.value)}
          style={selectStyle}
        >
          <option value="">Все тарифы</option>
          {plansOptions.map(code => (
            <option key={code} value={code}>{code}</option>
          ))}
        </select>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ flex: 1, fontSize: 11.5, color: C.dim }}>
          {loading ? 'Загрузка…' : `Записей: ${rows.length}`}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn small ghost icon={RefreshCw} color={C.muted} onClick={reload}>Обновить</Btn>
          <Btn small ghost icon={RotateCcw} color={C.red} disabled={resetting} onClick={doReset}>
            {resetting ? '…' : 'Сбросить'}
          </Btn>
          <Btn small icon={Download} disabled={exporting || (!rows.length && !t.totalCount)} onClick={doExport}>
            {exporting ? '…' : 'CSV'}
          </Btn>
        </div>
      </div>

      <ErrorLine onRetry={reload}>{error}</ErrorLine>

      {/* Плашка сброса: показывается, только когда сброс активен. Объясняет,
          почему цифры маленькие, и даёт вернуть историю одной кнопкой. */}
      {data?.resetAt ? (
        <Card style={{ padding: 12, marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <RotateCcw size={14} color={C.red} style={{ marginTop: 1, flexShrink: 0 }} />
            <div style={{ flex: 1, fontSize: 11.5, color: C.muted, lineHeight: 1.45 }}>
              Статистика сброшена <b style={{ color: '#fff' }}>{fmtDateTime(data.resetAt)}</b>.
              Цифры считаются только по платежам после этой даты. Записи об оплатах
              сохранены — CSV по-прежнему выгружает всю историю.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <Btn
              small
              ghost
              color={includeHistory ? C.accent : C.muted}
              onClick={() => setIncludeHistory(v => !v)}
            >
              {includeHistory ? 'Скрыть записи до сброса' : 'Показать записи до сброса'}
            </Btn>
            <Btn small ghost color={C.muted} disabled={resetting} onClick={undoReset}>
              Вернуть как было
            </Btn>
          </div>
        </Card>
      ) : null}

      {/* Сводные карточки */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
        <Card style={{ padding: 12, marginBottom: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <CheckCircle2 size={14} color={C.accent} />
            <span style={{ fontSize: 11, color: C.dim }}>Подтверждено</span>
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.accent, fontFamily: 'monospace' }}>
            ${fmtMoney(t.confirmedUsdt)}
          </div>
          <div style={{ fontSize: 10.5, color: C.dim, marginTop: 2 }}>
            {fmtInt(t.confirmedCount)} оплат
          </div>
        </Card>
        <Card style={{ padding: 12, marginBottom: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <Clock size={14} color={C.warn} />
            <span style={{ fontSize: 11, color: C.dim }}>Ожидают оплаты</span>
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.warn, fontFamily: 'monospace' }}>
            ${fmtMoney(t.pendingUsdt)}
          </div>
          <div style={{ fontSize: 10.5, color: C.dim, marginTop: 2 }}>
            {fmtInt(t.pendingCount)} заявок
          </div>
        </Card>
        <Card style={{ padding: 12, marginBottom: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <Wallet size={14} color={C.indigo} />
            <span style={{ fontSize: 11, color: C.dim }}>Всего по фильтру</span>
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#fff', fontFamily: 'monospace' }}>
            ${fmtMoney(t.totalUsdt)}
          </div>
          <div style={{ fontSize: 10.5, color: C.dim, marginTop: 2 }}>
            {fmtInt(t.totalCount)} транзакций
          </div>
        </Card>
        <Card style={{ padding: 12, marginBottom: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <ArrowDownUp size={14} color={C.muted} />
            <span style={{ fontSize: 11, color: C.dim }}>Сетей в выборке</span>
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#fff', fontFamily: 'monospace' }}>
            {fmtInt(networks.length)}
          </div>
          <div style={{ fontSize: 10.5, color: C.dim, marginTop: 2 }}>
            по {data?.byPlan?.length || 0} тарифам
          </div>
        </Card>
      </div>

      {/* Разбивки */}
      {data?.byNetwork?.length > 0 && (
        <Card title="По сетям" icon={Wallet} style={{ marginTop: 0 }}>
          {data.byNetwork.map(n => (
            <Row
              key={n.network}
              label={`${NETWORK_LABELS[n.network] || n.network} · ${n.c} шт`}
              value={`$${fmtMoney(n.amount)}`}
              valueColor={C.accent}
            />
          ))}
        </Card>
      )}

      {data?.byPlan?.length > 0 && (
        <Card title="По тарифам" icon={Wallet}>
          {data.byPlan.map(p => (
            <Row
              key={p.code}
              label={`${p.title} · ${p.c} шт`}
              value={`$${fmtMoney(p.amount)}`}
              valueColor={C.accent}
            />
          ))}
        </Card>
      )}

      {data?.byDay?.length > 0 && (
        <Card title="По дням (подтверждённый USDT)" icon={ArrowDownUp}>
          {data.byDay.map(d => (
            <Row
              key={d.day}
              label={`${d.day} · ${d.count} шт`}
              value={`$${fmtMoney(d.paidUsdt)}`}
              valueColor={C.green}
            />
          ))}
        </Card>
      )}

      {/* Реестр транзакций */}
      <Card title="Транзакции" icon={ArrowDownUp}>
        {loading && !rows.length ? <Loading /> : !rows.length ? (
          <Empty>Транзакций по выбранным фильтрам нет</Empty>
        ) : (
          <div style={{ margin: '0 -16px', padding: '0 16px' }}>
            {rows.map((r, i) => {
              const st = STATUS[r.status] || { label: r.status, color: C.muted }
              const open = openId === r.id
              return (
                <div key={r.id} style={{ borderTop: i ? `1px solid ${C.line}` : 'none', padding: '10px 0' }}>
                  <div
                    onClick={() => setOpenId(open ? null : r.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 600, color: '#fff' }}>
                        ${fmtMoney(r.amount_usdt)} USDT · {r.plan_title}
                      </div>
                      <div style={{ fontSize: 10.5, color: C.dim, marginTop: 2 }}>
                        {userLabel(r)} (id {r.user_id}) · {fmtDateTime(r.created_at)}
                      </div>
                    </div>
                    <Pill color={st.color}>{st.label}</Pill>
                  </div>

                  {open && (
                    <div style={{ marginTop: 8, background: C.inner, borderRadius: 12, padding: '8px 12px' }}>
                      <Row label="Сеть" value={NETWORK_LABELS[r.network] || r.network} />
                      <Row label="К оплате" value={`${r.amount_coin ?? '—'} ${r.currency || ''}`} mono />
                      <Row label="Создана" value={fmtDateTime(r.created_at)} />
                      <Row label="Действует до" value={fmtDateTime(r.expires_at)} />
                      {r.paid_at && <Row label="Оплачена" value={fmtDateTime(r.paid_at)} valueColor={C.accent} />}
                      {r.tx_hash && <Row label="Хэш" value={r.tx_hash} mono />}
                      {r.confirmed_amount != null && <Row label="Пришло" value={`${r.confirmed_amount}`} mono />}
                      <Row label="Подписка выдана" value={r.activated ? 'да' : 'нет'} valueColor={r.activated ? C.accent : C.dim} />
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </Card>
    </div>
  )
}
